'use client';

import Image from 'next/image';
import { ImageType } from '@/types/header';
import { useEffect, useRef, useState } from 'react';
import { isStrapiVideoMedia, resolveStrapiMediaUrl } from '@/lib/strapi-media-url';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { useMediaInViewport } from '@/hooks/useMediaInViewport';

interface ImageWithFallbackProps {
  image: ImageType;
  className?: string;
  alt?: string;
  width?: number;
  height?: number;
  fill?: boolean;
  priority?: boolean;
  fallbackSrc?: string;
  sizes?: string;
  /**
   * `media` (default): show brand photo placeholder while loading / on error.
   * `icon`: logos, social icons, UI icons — never show the brand photo placeholder.
   */
  variant?: 'media' | 'icon';
  /** Detach video URLs until the media enters the viewport. Defaults to true unless priority. */
  deferVideoLoad?: boolean;
}

export default function ImageWithFallback({
  image,
  className = '',
  alt,
  width,
  height,
  fill = false,
  priority = false,
  fallbackSrc,
  sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
  variant = 'media',
  deferVideoLoad,
}: ImageWithFallbackProps) {
  const isIcon = variant === 'icon';
  const resolvedFallback =
    fallbackSrc || (isIcon ? '/images/wonder-cement-logo.svg' : MEDIA_PLACEHOLDER);
  const resolvedUrl = resolveStrapiMediaUrl(image.url);
  const [imgSrc, setImgSrc] = useState<string>(resolvedUrl || resolvedFallback);
  const [hasError, setHasError] = useState(!resolvedUrl);
  const [isLoaded, setIsLoaded] = useState(!resolvedUrl || isIcon);
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const resolvedWidth = width || image.width || 1000;
  const resolvedHeight = height || image.height || 400;
  const isResponsive = width === undefined && height === undefined;
  const isVideo = isStrapiVideoMedia(image);
  const showingFallback = hasError || imgSrc === resolvedFallback;
  const shouldDeferVideo = deferVideoLoad ?? !priority;
  const { targetRef, shouldLoadMedia } = useMediaInViewport<HTMLSpanElement>({
    disabled: !shouldDeferVideo,
  });

  useEffect(() => {
    setVideoReady(
      Boolean(
        videoRef.current?.readyState &&
        videoRef.current.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA,
      ),
    );
  }, [imgSrc]);

  const handleError = () => {
    if (!hasError) {
      setHasError(true);
      setIsLoaded(true);
      if (image.url.includes('mail_icon') || alt?.includes('mail')) {
        setImgSrc('/icons/mail.svg');
      } else if (image.url.includes('phone_icon') || alt?.includes('phone')) {
        setImgSrc('/icons/phone.svg');
      } else if (image.url.includes('search_icon') || alt?.includes('search')) {
        setImgSrc('/icons/search.svg');
      } else if (
        isIcon ||
        image.url.includes('brand_logo') ||
        image.url.includes('logo') ||
        alt?.toLowerCase().includes('logo')
      ) {
        setImgSrc(fallbackSrc || '/images/wonder-cement-logo.svg');
      } else {
        setImgSrc(resolvedFallback);
      }
    }
  };

  if (isVideo && !isIcon) {
    return (
      <span
        ref={targetRef}
        className={fill ? 'absolute inset-0 block overflow-hidden' : 'relative block w-full h-full'}
      >
        {!videoReady && (
          <Image
            src={resolvedFallback}
            alt=""
            aria-hidden
            fill
            className={`object-cover ${className}`.trim()}
            sizes={sizes}
            priority={priority}
            unoptimized
          />
        )}
        <video
          ref={videoRef}
          src={shouldLoadMedia ? imgSrc : undefined}
          aria-label={alt || image.alt || undefined}
          className={`pointer-events-none transition-opacity duration-300 ${className || ''}`.trim()}
          width={fill ? undefined : resolvedWidth}
          height={fill ? undefined : resolvedHeight}
          autoPlay={shouldLoadMedia}
          muted
          loop
          playsInline
          controls={false}
          disablePictureInPicture
          preload={shouldLoadMedia ? (priority ? 'auto' : 'metadata') : 'none'}
          poster={resolvedFallback}
          style={
            fill
              ? {
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  opacity: videoReady ? 1 : 0,
                }
              : isResponsive
                ? { width: '100%', height: '100%', opacity: videoReady ? 1 : 0 }
                : { opacity: videoReady ? 1 : 0 }
          }
          onLoadedData={() => setVideoReady(true)}
          onCanPlay={() => setVideoReady(true)}
          onPlaying={() => setVideoReady(true)}
          onError={() => setVideoReady(false)}
        />
      </span>
    );
  }

  return (
    <Image
      src={imgSrc}
      alt={alt || image.alt || ''}
      fill={fill}
      width={fill ? undefined : resolvedWidth}
      height={fill ? undefined : resolvedHeight}
      className={className}
      priority={priority}
      onError={handleError}
      onLoad={() => setIsLoaded(true)}
      sizes={sizes}
      style={{
        ...(!fill && isResponsive ? { width: '100%', height: '100%' } : {}),
        ...(!isIcon && !isLoaded && !showingFallback
          ? {
              backgroundImage: `url('${resolvedFallback}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }
          : {}),
      }}
      unoptimized={showingFallback}
    />
  );
}
